// Travel-time overlay: a real OpenRouteService isochrone polygon (walk/bike/
// drive) with a Haversine-circle fallback (transit, or ORS failure).
//
// The live circle/polygon/cache/fetchSeq are module state — NOT Svelte stores
// (you can't react to a google.maps.Polygon mutation anyway). `travelApprox`
// IS a store so the panel can show the "approximate" badge.

import { derived, writable } from 'svelte/store';
import { HEURISTIC_SPEEDS, MODE_COLORS, ORS_PROFILES, isInRegion } from '../scoring';
import { friends, trips } from '../state';
import type { Friend, LatLng, TravelMode, TravelRegion, TravelTimeSettings, Trip } from '../types';

/** True when the overlay is a circle approximation rather than a real isochrone. */
export const travelApprox = writable(false);

/** Geometry of the currently-drawn radius, or null when nothing is shown. */
export const travelRegion = writable<TravelRegion | null>(null);

/** The people who fall within the active candidate's travel-time radius. */
export const reachableFriends = derived(
  [friends, travelRegion],
  ([$friends, $region]): Friend[] =>
    $region ? $friends.filter((f) => isInRegion(f, $region)) : [],
);

/** The favorite spots that fall within the active candidate's travel-time radius. */
export const reachableTrips = derived(
  [trips, travelRegion],
  ([$trips, $region]): Trip[] =>
    $region ? $trips.filter((t) => isInRegion(t, $region)) : [],
);

let circle: google.maps.Circle | null = null;
let polygon: google.maps.Polygon | null = null;
let fetchSeq = 0;
let fetchTimer: ReturnType<typeof setTimeout> | null = null;

// Wait this long after the last change before hitting ORS. The circle + counts
// update instantly (no network), so dragging the slider stays responsive while
// only the final value triggers an isochrone request — avoids 429 rate limits.
const ISOCHRONE_DEBOUNCE_MS = 400;

// ---- Per-user isochrone cache (persisted) ----
//
// An isochrone is a pure function of (profile, point, minutes) and never
// changes, so once a user has fetched one for a given candidate + mode + time
// we never need to ask ORS again — including across reloads. We keep this in
// localStorage (NOT the synced state doc) so it stays per-browser and can't
// bloat the Firestore document. Cross-user caching is deliberately skipped:
// candidates sit at unique addresses, so shared hits would be near zero.
const CACHE_STORAGE_KEY = 'sfhome:isochrones';
const CACHE_MAX = 250; // bounded; oldest entries evicted first (insertion order)
const cache = new Map<string, LatLng[]>();

// When ORS rate-limits us (429), back off for this long and serve circles only,
// instead of flooding the network tab with failing requests.
const COOLDOWN_MS = 60_000;
let cooldownUntil = 0;

function loadCache(): void {
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw) as [string, LatLng[]][];
    if (Array.isArray(entries)) {
      for (const [k, ring] of entries) {
        if (typeof k === 'string' && Array.isArray(ring)) cache.set(k, ring);
      }
    }
  } catch {
    /* corrupt/unavailable storage — start empty */
  }
}
loadCache();

let saveTimer: ReturnType<typeof setTimeout> | null = null;
function saveCache(): void {
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    try {
      localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify([...cache.entries()]));
    } catch {
      /* quota exceeded or unavailable — cache simply won't persist */
    }
  }, 500);
}

/** Drop the persisted isochrone cache (called on sign-out so it can't linger). */
export function clearIsochroneCache(): void {
  cache.clear();
  try { localStorage.removeItem(CACHE_STORAGE_KEY); } catch { /* ignore */ }
}

function cachePut(key: string, ring: LatLng[]): void {
  // Round to ~1m to keep the persisted payload small.
  const slim = ring.map((p) => ({ lat: +p.lat.toFixed(5), lng: +p.lng.toFixed(5) }));
  cache.set(key, slim);
  while (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value as string);
  saveCache();
}

function travelRadiusMeters(mode: TravelMode, minutes: number): number {
  const s = HEURISTIC_SPEEDS;
  const minPerKm =
    mode === 'WALKING' ? s.walkMinPerKm
    : mode === 'BICYCLING' ? s.bikeMinPerKm
    : mode === 'TRANSIT' ? s.transitMinPerKm
    : s.driveMinPerKm;
  const km = (minutes / minPerKm) * 0.7; // 0.7 detour factor
  return km * 1000;
}

function cacheKey(lat: number, lng: number, mode: TravelMode, minutes: number): string {
  return `${lat.toFixed(4)},${lng.toFixed(4)}|${mode}|${minutes}`;
}

async function fetchIsochronePath(lat: number, lng: number, mode: TravelMode, minutes: number): Promise<LatLng[] | null> {
  const profile = ORS_PROFILES[mode];
  if (!profile) return null; // transit/unknown → caller falls back to circle
  const key = cacheKey(lat, lng, mode, minutes);
  const cached = cache.get(key);
  if (cached) return cached;
  if (Date.now() < cooldownUntil) return null; // rate-limited recently → circle only
  try {
    const resp = await fetch('/api/isochrone', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ profile, locations: [[lng, lat]], minutes }),
    });
    if (resp.status === 429) {
      cooldownUntil = Date.now() + COOLDOWN_MS; // back off the shared ORS quota
      return null;
    }
    if (!resp.ok) return null;
    const data = await resp.json();
    const ring = data?.features?.[0]?.geometry?.coordinates?.[0];
    if (!Array.isArray(ring)) return null;
    const path: LatLng[] = ring.map(([lo, la]: [number, number]) => ({ lat: la, lng: lo }));
    cachePut(key, path);
    return cache.get(key) ?? path;
  } catch {
    return null;
  }
}

export function clearTravelOverlay(): void {
  if (circle) { circle.setMap(null); circle = null; }
  if (polygon) { polygon.setMap(null); polygon = null; }
  if (fetchTimer) { clearTimeout(fetchTimer); fetchTimer = null; }
  travelRegion.set(null);
}

function renderCircle(map: google.maps.Map, center: LatLng, color: string, mode: TravelMode, minutes: number): void {
  if (polygon) { polygon.setMap(null); polygon = null; }
  const radius = travelRadiusMeters(mode, minutes);
  const style = { strokeColor: color, strokeOpacity: 0.95, strokeWeight: 3, fillColor: color, fillOpacity: 0.18 };
  if (!circle) {
    circle = new google.maps.Circle({ map, center, radius, clickable: false, zIndex: 1, ...style });
  } else {
    circle.setOptions({ center, radius, ...style });
    circle.setMap(map);
  }
}

function renderPolygon(map: google.maps.Map, path: LatLng[], color: string): void {
  if (circle) { circle.setMap(null); circle = null; }
  if (!polygon) {
    polygon = new google.maps.Polygon({
      map, paths: path, clickable: false, zIndex: 1,
      strokeColor: color, strokeOpacity: 0.9, strokeWeight: 2, fillColor: color, fillOpacity: 0.18,
    });
  } else {
    polygon.setOptions({ paths: path, strokeColor: color, fillColor: color });
    polygon.setMap(map);
  }
}

/** Live circle that follows the cursor while a candidate is being dragged. */
export function dragOverlay(map: google.maps.Map, center: LatLng, settings: TravelTimeSettings): void {
  if (!settings.enabled) return;
  const color = MODE_COLORS[settings.mode] || '#5a8a3a';
  renderCircle(map, center, color, settings.mode, settings.minutes);
}

/** Reconcile the overlay to the active candidate + current settings. */
export function updateTravelOverlay(
  map: google.maps.Map,
  candidate: LatLng | null,
  settings: TravelTimeSettings,
): void {
  if (!candidate || !settings.enabled) {
    clearTravelOverlay();
    travelApprox.set(false);
    return;
  }
  const color = MODE_COLORS[settings.mode] || '#5a8a3a';
  // Bump the sequence on every change so any in-flight or pending fetch that
  // resolves later is recognised as stale and skipped.
  const seq = ++fetchSeq;
  const orsSupported = !!ORS_PROFILES[settings.mode];
  travelApprox.set(!orsSupported);
  renderCircle(map, candidate, color, settings.mode, settings.minutes);
  // Publish the circle region immediately so the "who's within reach" count
  // appears without waiting on the network; the polygon refines it below.
  travelRegion.set({
    kind: 'circle',
    center: candidate,
    radiusMeters: travelRadiusMeters(settings.mode, settings.minutes),
  });

  if (fetchTimer) { clearTimeout(fetchTimer); fetchTimer = null; }
  if (!orsSupported) return; // transit/unknown → circle only, no ORS request

  // Cached values resolve synchronously enough to skip the debounce wait.
  const debounce = cache.has(cacheKey(candidate.lat, candidate.lng, settings.mode, settings.minutes))
    ? 0
    : ISOCHRONE_DEBOUNCE_MS;
  fetchTimer = setTimeout(() => {
    fetchTimer = null;
    fetchIsochronePath(candidate.lat, candidate.lng, settings.mode, settings.minutes).then((path) => {
      if (seq !== fetchSeq || !settings.enabled) return;
      if (!path) {
        travelApprox.set(true);
        return;
      }
      travelApprox.set(false);
      renderPolygon(map, path, color);
      travelRegion.set({ kind: 'polygon', path });
    });
  }, debounce);
}
